import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { resources } from '../../../core/services/resources';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import type { EntityTypeEnrichment, EntityTypeEnrichmentSection } from '../../../core/types/data-models';
import { SourcesBlock } from '../../../shared/components/SourcesBlock';
import { InsightTopicSectionRow, type ExpandSignal } from './InsightTopicSectionRow';

/**
 * One AI-generated insight topic's tab content — description, the topic's
 * heading sections as a collapsible tree with a collapse/expand-all toggle
 * (like DocumentDetail's), and its cited sources. Keyed by topic id in the
 * parent, so switching topics starts collapsed again.
 */
export function InsightTopicTab({ topic }: { topic: EntityTypeEnrichment }) {
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandSignal, setExpandSignal] = useState<ExpandSignal | null>(null);

  const sectionsQuery = useQuery({
    queryKey: ['managed-knowledge-enrichment-sections', topic.id],
    queryFn: () =>
      resources.entityTypeEnrichmentSections.list({
        filter: { entityTypeEnrichmentId: [topic.id] },
        sort: 'order',
        pageSize: 1000,
      }),
  });
  const sections: EntityTypeEnrichmentSection[] = sectionsQuery.data?.data ?? [];

  // Rows arrive sorted by `order`, so both lists keep document order.
  const rootSections = sections.filter((s) => s.parentSectionId === null);
  const childrenByParent = new Map<number, EntityTypeEnrichmentSection[]>();
  for (const s of sections) {
    if (s.parentSectionId === null) continue;
    const list = childrenByParent.get(s.parentSectionId) ?? [];
    list.push(s);
    childrenByParent.set(s.parentSectionId, list);
  }

  const handleToggleAll = () => {
    const next = !allExpanded;
    setExpandSignal((prev) => ({ value: next, tick: (prev?.tick ?? 0) + 1 }));
    setAllExpanded(next);
  };

  return (
    <GlassPanel sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {topic.topicDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {topic.topicDescription}
            </Typography>
          )}
        </Box>
        <Tooltip title={allExpanded ? 'Collapse all sections' : 'Expand all sections'}>
          <span>
            <IconButton size="small" onClick={handleToggleAll} disabled={sections.length === 0}>
              {allExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {sectionsQuery.isLoading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={36} />
          ))}
        </Stack>
      ) : sections.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          This topic has no content yet — regenerate it to research these entities again.
        </Typography>
      ) : (
        <Box>
          {rootSections.map((s) => (
            <InsightTopicSectionRow
              key={s.id}
              section={s}
              childrenByParent={childrenByParent}
              depth={0}
              expandSignal={expandSignal}
            />
          ))}
        </Box>
      )}

      <SourcesBlock sources={topic.topicSources} />
    </GlassPanel>
  );
}
